import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getAccountData, updateAccountData, handleCheckout } from '../../api/BackendApi';

const InputField = ({ label, name, value, onChange, required = false }) => {
  const handleInvalid = (e) => {
    if (e.target.validity.valueMissing) {
      e.target.setCustomValidity('Toto pole je povinné.');
    } else if (e.target.validity.tooLong) {
      e.target.setCustomValidity('Maximální délka je 50 znaků.');
    } else {
      e.target.setCustomValidity('');
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && '*'}
      </label>
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        maxLength={50}
        onInvalid={handleInvalid}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
};

const FillInvoiceDataPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const amount = location.state?.amount;
  const fileId = location.state?.fileId;

  const [formData, setFormData] = useState({
    name: '', street: '', houseNumber: '', city: '',
    postalCode: '', country: 'Česká republika',
    ico: '', dic: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!amount) {
      navigate(-1); // Vrátí uživatele zpět, pokud data chybí
    }
  }, [amount, fileId, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getAccountData();
        setFormData({
          name: data.name || '', street: data.street || '', houseNumber: data.houseNumber || '',
          city: data.city || '', postalCode: data.postalCode || '', country: data.country || 'Česká republika',
          ico: data.ico || '', dic: data.dic || ''
        });
      } catch (err) {
        setError('Nepodařilo se načíst data k úpravě.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    e.target.setCustomValidity('');
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    try {
      await updateAccountData(formData);
      handleCheckout(amount, fileId)
    } catch (err) {
      setError(err.message || err.error || 'Při ukládání došlo k chybě.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="flex justify-center py-12">Načítám...</div>;

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-6" noValidate={false}>
          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">{error}</div>}

          <h2 className="text-lg font-semibold text-gray-800 mb-4">Vyplňte fakturační údaje</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="md:col-span-2">
              <InputField label="Název společnosti / Jméno a příjmení" name="name" value={formData.name} onChange={handleChange} required />
            </div>
            <InputField label="IČO (nepovinné)" name="ico" value={formData.ico} onChange={handleChange} />
            <InputField label="DIČ (nepovinné)" name="dic" value={formData.dic} onChange={handleChange} />
          </div>

          <h2 className="text-lg font-medium text-gray-800 mb-2">Adresa</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <InputField label="Ulice" name="street" value={formData.street} onChange={handleChange} required />
            <InputField label="Číslo popisné" name="houseNumber" value={formData.houseNumber} onChange={handleChange} required />
            <InputField label="Město" name="city" value={formData.city} onChange={handleChange} required />
            <InputField label="PSČ" name="postalCode" value={formData.postalCode} onChange={handleChange} required />
            <InputField label="Země" name="country" value={formData.country} onChange={handleChange} required />
          </div>

          <div className="flex items-center justify-end gap-4 mt-8 pt-4 border-t border-gray-100">
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm transition-colors"
            >
              Pokračovat k platbě
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FillInvoiceDataPage;